import { useState, Fragment } from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { useIntl, FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { N10 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import { DataEventProps } from "@/libraries/wpl/foundations/src";
import { Play } from "@/libraries/wpl/foundations/src/icons/object/play";
import { YoutubeModal } from "@/patterns/modal/src";

const videoSectionStyle = css`
    background: linear-gradient(
        180deg,
        transparent 50%,
        #e6fcff 50%,
        #e6fcff 100%
    );
    position: relative;
`;

const videoCardWrapperStyles = css`
    padding: ${token("space.200")};
    margin-left: auto;
    margin-right: auto;
    display: flex;
    justify-content: center;

    @media (min-width: 540px) {
        width: 66.66666%;
    }

    ${media.above.sm} {
        width: 83.33333%;
    }

    ${media.above.md} {
        width: 66.66666%;
    }
`;

const videoCardContainerStyles = css`
    display: flex;
    flex-direction: column;
    box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
    background-color: ${N10};
    border-radius: 8px;
    max-width: 767.99px;
    overflow: hidden;

    ${media.above.sm} {
        flex-direction: row;
    }
`;

const videoCardImageStyles = css`
    position: relative;
    padding: 0 ${token("space.200")};
    overflow: hidden;

    ${media.above.sm} {
        flex: 5;
    }

    ${media.above.md} {
        height: 184px;
        padding-left: 28px;
        padding-right: 28px;
    }

    :hover::before {
        background-color: #ffffff;
        transform: scale(1.06);
    }

    ::before {
        position: absolute;
        content: "";
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        margin: 0;
        background-position: center center;
        background-size: cover;
        background-color: rgba(23, 43, 77, 0.15);
        background-blend-mode: multiply;
        transform-origin: center center;
        transform: scale(1);
        transition: all 1.3s;
        background-image: url(https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:83995a77-0155-4484-b349-39c9501f3313/Confluence%20PT%20thumbnail@2x.png?cdnVersion=1263);
        z-index: 1;
    }

    img {
        max-height: 340px;
        opacity: 0;
        width: 100%;
    }

    svg {
        z-index: 2;
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        margin: auto;
        fill: rgba(255, 255, 255, 0.9);
    }
`;

const videoCardTextStyles = css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    padding: ${token("space.400")};

    h3 {
        color: #253858;
        margin-top: 0;
        font-size: ${token("space.250")};
        letter-spacing: 0.3px;
        line-height: 1.2;
        margin-bottom: 14px;
        font-weight: 500;
    }

    ${media.above.sm} {
        flex: 7;

        h3 {
            font-size: ${token("space.300")};
        }
    }

    ${media.above.md} {
        padding: ${token("space.500")};

        h3 {
            line-height: ${token("space.400")};
        }
    }

    a {
        display: flex;
        align-items: center;
        color: #ffffff;

        span {
            font-size: ${token("space.200")};
            color: #0052cc;
        }

        svg {
            fill: #0052cc;
            margin-right: ${token("space.100")};
        }
    }
`;

const VideoSection = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const intl = useIntl();

    const videoCardDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "videoEmbedInline",
        "data-event": "clicked",
        "data-label": "watch the video_image"
    };

    const videoCardTextDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "videoLink",
        "data-event": "clicked",
        "data-label-english": "Watch the video",
        "data-label": "Watch the video"
    };

    return (
        <Fragment>
            <div css={videoSectionStyle}>
                <div css={videoCardWrapperStyles}>
                    <div css={videoCardContainerStyles}>
                        <a
                            css={videoCardImageStyles}
                            href="https://www.youtube.com/watch?v=jqg8o-ataIQ"
                            data-schema-version="1"
                            {...videoCardDataEventProps}
                            onClick={(event) => {
                                event.preventDefault();
                                setIsModalOpen(true);
                            }}
                        >
                            <img
                                src="https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_Thumbnail.webp"
                                alt={intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.Video.VideoThumbnailAltText",
                                    defaultMessage: "video thumbnail"
                                })}
                            />
                            <Play size="70" />
                        </a>
                        <div css={videoCardTextStyles}>
                            <h3>
                                <FormattedMessage
                                    id="ConfluenceSoftwarePageTemplate.Video.connects.VideoHeader"
                                    defaultMessage={
                                        "Learn how Confluence connects teams"
                                    }
                                />
                            </h3>
                            <a
                                css={css`
                                    :hover {
                                        svg,
                                        span {
                                            color: #0065ff;
                                            fill: #0065ff;
                                        }
                                    }
                                `}
                                href="https://www.youtube.com/watch?v=jqg8o-ataIQ"
                                {...videoCardTextDataEventProps}
                                data-schema-version="1"
                                onClick={(event) => {
                                    event.preventDefault();
                                    setIsModalOpen(true);
                                }}
                            >
                                <Play size="22" />
                                <span>
                                    <FormattedMessage
                                        id="ConfluenceSoftwarePageTemplate.Video.PlayVideo"
                                        defaultMessage={"Watch the video"}
                                    />
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <YoutubeModal
                isOpen={isModalOpen}
                src="https://www.youtube.com/embed/jqg8o-ataIQ?autoplay=1&rel=0&origin=https%3A%2F%2Fwww.atlassian.com&widget_referrer=https%3A%2F%2Fwww.atlassian.com&enablejsapi=1&widgetid=1"
                title={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.Video.ModalYouTubeIframeTitle",
                    defaultMessage:
                        "Say hello to Confluence! | Confluence Overview | Atlassian"
                })}
                closeIconLabel={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.Video.CloseModalButtonMobileLabel",
                    defaultMessage: "Close modal"
                })}
                closeModal={() => setIsModalOpen(false)}
            />
        </Fragment>
    );
};

export default VideoSection;
